import { useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useIxfAccountingV1ConfigQueryServiceConfigGet } from '@interxfi/api-client/generated/hooks/ixf-accounting-v1-config-query-service-config-get';
import { DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE, PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { ActionTileGrid, type ActionTileGridTile } from '@interxfi/design-system/components/action-tile/grid/action-tile-grid';
import AccountingIcon from '@interxfi/design-system/icons/react/icon-accounting';
import JournalIcon from '@interxfi/design-system/icons/react/icon-journal';
import LockIcon from '@interxfi/design-system/icons/react/icon-lock';
import PercentIcon from '@interxfi/design-system/icons/react/icon-percent';
import TransferIcon from '@interxfi/design-system/icons/react/icon-transfer';
import { AccountingConfigSetLockOutDialog } from '../../../../../../../features/accounting/config/accounting-config-set-lock-out-dialog';
import { ACCOUNTING_MANUAL_ENTRY_BASE_PATH } from '../../../../../../../features/accounting/manual-entry/accounting-manual-entry-routing';
import { FEE_RATE_BASE_PATH } from '../../../../../../../features/fees/fee-rate/fee-rate-routing';
import { getPageHead } from '../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/finance/_finance/')({
  head: () => getPageHead('Finance'),
  component: ClearingFinanceIndex,
});

function getTiles(currentLockDate: string | undefined) {
  return [
    {
      illustration: <AccountingIcon aria-hidden />,
      title: 'Accounting',
      subtitle: 'Chart of Accounts, General Ledger Activity, Balance Sheet, Trial Balance, Income Statement',
      to: '/clearing/finance/accounting',
    },
    {
      illustration: <TransferIcon aria-hidden />,
      title: 'Payments',
      subtitle: 'Company payments originated in the proprietary system',
      to: '/clearing/finance/payment',
    },
    {
      illustration: <JournalIcon aria-hidden />,
      title: 'Journals',
      subtitle: 'Manual Journal Entries',
      to: ACCOUNTING_MANUAL_ENTRY_BASE_PATH,
    },
    {
      illustration: <LockIcon aria-hidden />,
      title: 'Lock Date',
      subtitle: ['Lock Date for Journal Entries.', currentLockDate ? `Currently set to ${currentLockDate}.` : ''].filter(Boolean).join(' '),
      interaction: {
        type: 'modal',
        modal: { Component: AccountingConfigSetLockOutDialog, props: {} },
      },
    },
    {
      illustration: <PercentIcon aria-hidden />,
      title: 'Middle Money',
      subtitle: 'Fee Rates, TAF Reports',
      to: FEE_RATE_BASE_PATH,
    },
  ] as const satisfies ActionTileGridTile[];
}

function ClearingFinanceIndex() {
  // This is a special case where the config is a global singleton with the primary key 'global'
  const { data } = useIxfAccountingV1ConfigQueryServiceConfigGet({ global: 'global' });
  const tiles = useMemo(() => getTiles(data?.config?.data?.lockOutDate), [data?.config?.data?.lockOutDate]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <ActionTileGrid items={tiles} LinkElement={Link} />
      </PageLayout.Section>
    </PageLayout>
  );
}
